import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ShieldCheckIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  KeyIcon,
  CogIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'
import { hubnaiAPI } from '@/services/api'
import type { HubnaiCredentials } from '@/services/api'

const GatewayFinProSettings: React.FC = () => {
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [credentials, setCredentials] = useState<HubnaiCredentials | null>(null)

  const [formData, setFormData] = useState({
    api_key: '',
    api_secret: '',
    sandbox: true
  })

  const [testResult, setTestResult] = useState<{
    success: boolean
    message: string
  } | null>(null)

  useEffect(() => {
    loadCredentials()
  }, [])

  const loadCredentials = async () => {
    try {
      setLoading(true)
      const data = await hubnaiAPI.getCredentials()
      setCredentials(data)

      // Não preencher os campos de senha por segurança
      // Apenas indicar que existem credenciais
      if (data && data.active) {
        setFormData({
          api_key: '***********',
          api_secret: '***********',
          sandbox: data.sandbox
        })
      }
    } catch (error: any) {
      console.log('Nenhuma credencial configurada ainda')
    } finally {
      setLoading(false)
    }
  }

  const handleTestConnection = async () => {
    if (!formData.api_key) {
      setTestResult({
        success: false,
        message: 'Por favor, preencha a API Key'
      })
      return
    }

    // Se os campos estiverem mascarados, não testar
    if (formData.api_key === '***********') {
      setTestResult({
        success: false,
        message: 'Por favor, insira sua API Key real para testar'
      })
      return
    }

    try {
      setTesting(true)
      setTestResult(null)

      const result = await hubnaiAPI.testCredentials({
        api_key: formData.api_key,
        api_secret: formData.api_secret,
        sandbox: formData.sandbox
      })

      setTestResult({
        success: true,
        message: 'Conexão testada com sucesso! Credenciais válidas.'
      })
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.response?.data?.detail || 'Erro ao testar conexão. Verifique as credenciais.'
      })
    } finally {
      setTesting(false)
    }
  }

  const handleSave = async () => {
    if (!formData.api_key) {
      alert('Por favor, preencha a API Key')
      return
    }

    // Se os campos estiverem mascarados, não salvar
    if (formData.api_key === '***********') {
      alert('As credenciais já estão salvas. Para alterar, digite novas credenciais.')
      return
    }

    try {
      setSaving(true)

      await hubnaiAPI.saveCredentials({
        api_key: formData.api_key,
        api_secret: formData.api_secret,
        sandbox: formData.sandbox
      })

      alert('Credenciais salvas com sucesso!')
      navigate('/payments/gateway-finpro')
    } catch (error: any) {
      alert('Erro ao salvar credenciais: ' + (error.response?.data?.detail || error.message))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!credentials) return

    if (!confirm('Tem certeza que deseja remover as credenciais do Gateway FinPro? Esta ação não pode ser desfeita.')) {
      return
    }

    try {
      await hubnaiAPI.deleteCredentials()
      alert('Credenciais removidas com sucesso!')
      setCredentials(null)
      setFormData({
        api_key: '',
        api_secret: '',
        sandbox: true
      })
    } catch (error: any) {
      alert('Erro ao remover credenciais: ' + (error.response?.data?.detail || error.message))
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/payments/gateway-finpro')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Voltar"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl">
                  <CogIcon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Configurações do Gateway FinPro</h1>
                  <p className="text-sm text-gray-500">Configure suas credenciais da API Hubnai</p>
                </div>
              </div>
            </div>
            {credentials && credentials.active && (
              <span className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-sm font-medium">
                <CheckCircleIcon className="w-4 h-4" />
                Configurado
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <InformationCircleIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-2">Como obter sua API Key</h3>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Acesse o painel da Hubnai em <a href="https://app.hubnai.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">app.hubnai.com</a></li>
              <li>Vá em Configurações → API & Webhooks</li>
              <li>Copie sua API Key</li>
              <li>Cole aqui e clique em "Testar Conexão"</li>
              <li>Se funcionar, clique em "Salvar Configurações"</li>
            </ol>
            <p className="text-xs text-blue-700 mt-3">
              💡 <strong>Ambiente Sandbox:</strong> Use para testes. Ambiente de produção processa pagamentos reais.
            </p>
          </div>
        </div>
      </div>

      {/* Configuration Form */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Credenciais da API</h2>

          <div className="space-y-6">
            {/* API Key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <KeyIcon className="w-4 h-4" />
                  API Key
                </div>
              </label>
              <input
                type="text"
                value={formData.api_key}
                onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                placeholder="Digite sua API Key"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
              />
            </div>

            {/* API Secret */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <ShieldCheckIcon className="w-4 h-4" />
                  API Secret <span className="text-gray-400 font-normal">(Opcional)</span>
                </div>
              </label>
              <input
                type="password"
                value={formData.api_secret}
                onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
                placeholder="Deixe vazio se não usar"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                A Hubnai usa apenas API Key. Deixe este campo vazio.
              </p>
            </div>

            {/* Environment Toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Ambiente</label>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setFormData({ ...formData, sandbox: true })}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                    formData.sandbox
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold">🧪 Sandbox</div>
                  <div className="text-xs mt-1">Ambiente de testes</div>
                </button>
                <button
                  onClick={() => setFormData({ ...formData, sandbox: false })}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                    !formData.sandbox
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold">🚀 Produção</div>
                  <div className="text-xs mt-1">Pagamentos reais</div>
                </button>
              </div>
            </div>

            {/* Test Result */}
            {testResult && (
              <div className={`p-4 rounded-lg border ${
                testResult.success
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : 'bg-red-50 border-red-200 text-red-800'
              }`}>
                <div className="flex items-center gap-2">
                  {testResult.success ? (
                    <CheckCircleIcon className="w-5 h-5" />
                  ) : (
                    <XCircleIcon className="w-5 h-5" />
                  )}
                  <span className="text-sm font-medium">{testResult.message}</span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center gap-3 pt-4">
              <button
                onClick={handleTestConnection}
                disabled={testing}
                className="flex-1 px-6 py-3 bg-white border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
              >
                {testing ? (
                  <>
                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    Testando...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="w-5 h-5" />
                    Testar Conexão
                  </>
                )}
              </button>

              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg font-medium flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <ShieldCheckIcon className="w-5 h-5" />
                    Salvar Configurações
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      {credentials && credentials.active && (
        <div className="bg-white rounded-xl border-2 border-red-200 shadow-sm">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-red-900 mb-2">Zona de Perigo</h2>
            <p className="text-sm text-gray-600 mb-4">
              Remover as credenciais desativará completamente o Gateway FinPro. Você perderá acesso a todas as funcionalidades.
            </p>
            <button
              onClick={handleDelete}
              className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              Remover Credenciais
            </button>
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Precisa de ajuda?</h2>
          <div className="space-y-3 text-sm text-gray-600">
            <p>
              📚 <strong>Documentação:</strong>{' '}
              <a href="https://docs.hubnai.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                docs.hubnai.com
              </a>
            </p>
            <p>
              💬 <strong>Suporte:</strong> suporte@hubnai.com
            </p>
            <p>
              🔒 <strong>Segurança:</strong> Suas credenciais são criptografadas e armazenadas com segurança.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GatewayFinProSettings
